<template>
  <div class="data-storage">
    <CRow class="mb-4">
      <CCol>
        <label v-translate translate-context="form_configurator.data_storage.label">Storage Provider</label>
        <CSelect 
          :value="selectedStorage" 
          @change="onStorageChange"
          :options="storageOptions"
          :placeholder="$pgettext('form_configurator.data_storage', 'Select storage provider')"
        />
      </CCol>
    </CRow>

    <!-- Pica Storage Information -->
    <div v-if="selectedStorage === dataStorageOptions.PICA_STORAGE" class="mt-3">
      <PAlert color="primary" class="mb-3">
        <h6 class="mb-1" v-translate translate-context="form_configurator.data_storage">Pica Storage</h6>
        <p v-translate translate-context="form_configurator.data_storage">
          Form data will be securely stored in Pica's cloud infrastructure. 
          All data is encrypted at rest and in transit, ensuring maximum security and privacy compliance. 
          Pica will handle all data processing, storage, and retention policies according to our terms of service.
        </p>
        <small class="text-muted" v-translate translate-context="form_configurator.data_storage">
          Data stored with Pica Storage is automatically backed up and maintained for reliability.
        </small>
      </PAlert>
    </div>

    <!-- JSON Endpoint Configuration -->
    <div v-if="selectedStorage === dataStorageOptions.JSON_ENDPOINT" class="mt-3">
      <PAlert color="warning" class="mb-3">
        <h6 class="mb-1" v-translate translate-context="form_configurator.data_storage">JSON Endpoint Integration</h6>
        <p v-translate translate-context="form_configurator.data_storage">
          Form data will be sent to your specified endpoint as JSON. 
          Configure the endpoint URL and JSON pattern below.
        </p>
      </PAlert>
      
      <CRow class="mb-3">
        <CCol>
          <label v-translate translate-context="form_configurator.data_storage.json_endpoint">Endpoint URL</label>
          <CInput 
            v-model="jsonConfig.endpoint" 
            @input="save"
            :placeholder="$pgettext('form_configurator.data_storage.json_endpoint', 'https://api.example.com/webhook')"
          />
          <small class="text-muted" v-translate translate-context="form_configurator.data_storage.json_endpoint">
            The URL where form data will be sent via POST request
          </small>
        </CCol>
      </CRow>
      
      <CRow class="mb-3">
        <CCol>
          <label v-translate translate-context="form_configurator.data_storage.json_endpoint">JSON Pattern</label>
          <CTextarea 
            v-model="jsonConfig.pattern" 
            @input="onPatternInput"
            :placeholder="jsonPatternPlaceholder"
            rows="6"
          />
          <small class="text-muted">
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint">Use </span>
            <span v-pre>{{FIELD_NAME}}</span>
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint"> to reference form fields (e.g., </span>
            <span v-pre>{{nome_utente}}</span>
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint">, </span>
            <span v-pre>{{email}}</span>
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint">)</span>
            <br>
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint">System fields: </span>
            <span v-pre>{{created_at}}</span>
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint"> (current timestamp)</span>
            <br>
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint">Date modifiers: </span>
            <span v-pre>{{date|iso}}</span>
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint">, </span>
            <span v-pre>{{date|'YYYY-MM-DD'}}</span>
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint">, </span>
            <span v-pre>{{date|'DD/MM/YYYY'}}</span>
            <br>
            <span v-translate translate-context="form_configurator.data_storage.json_endpoint">Common formats: YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD HH:mm:ss</span>
          </small>
        </CCol>
      </CRow>
      
      <CRow class="mb-3">
        <CCol>
          <label v-translate translate-context="form_configurator.data_storage.json_endpoint">
            Preview
            <span v-if="jsonConfig.pattern && isJsonValid" class="text-success ml-2">✓</span>
            <span v-else-if="jsonConfig.pattern && !isJsonValid" class="text-danger ml-2">✗</span>
          </label>
          <CTextarea 
            v-model="jsonPreview" 
            rows="6"
            disabled
            class="preview-textarea"
            :class="{'is-valid': jsonConfig.pattern && isJsonValid, 'is-invalid': jsonConfig.pattern && !isJsonValid}"
          />
          <small v-if="jsonConfig.pattern && !isJsonValid" class="text-danger" v-translate translate-context="form_configurator.data_storage.json_endpoint">
            Invalid JSON format. Please check your pattern.
          </small>
        </CCol>
      </CRow>
      
      <!-- Debug Section -->
      <CRow class="mb-3">
        <CCol>
          <CCard class="bg-light">
            <CCardHeader>
              <h6 class="mb-0" v-translate translate-context="form_configurator.data_storage.json_endpoint">Debug Information</h6>
            </CCardHeader>
            <CCardBody>
              <p class="mb-2" v-translate translate-context="form_configurator.data_storage.json_endpoint">
                Test endpoint URL (for testing integration):
              </p>
              <div class="d-flex align-items-center mb-3">
                <code class="flex-grow-1 p-2 bg-white border rounded">{{ testEndpointUrl }}</code>
                <CButton 
                  color="secondary" 
                  size="sm" 
                  class="ml-2"
                  @click="copyTestUrl"
                  v-translate
                  translate-context="form_configurator.data_storage.json_endpoint"
                >
                  Copy
                </CButton>
              </div>
              
              <div class="mt-3">
                <CButton 
                  color="info" 
                  size="sm"
                  @click="checkLastJsonSent"
                  :disabled="checkingLastJson"
                  v-translate
                  translate-context="form_configurator.data_storage.json_endpoint"
                >
                  Check Last JSON Sent
                </CButton>
              </div>
              
              <div v-if="lastJsonResult" class="mt-3">
                <label v-translate translate-context="form_configurator.data_storage.json_endpoint">Last JSON sent:</label>
                <pre class="bg-white border rounded p-2 mb-0" style="max-height: 200px; overflow-y: auto; color: #000;">{{ lastJsonResult }}</pre>
              </div>
              
              <CAlert v-if="lastJsonError" color="danger" class="mt-3 mb-0">
                {{ lastJsonError }}
              </CAlert>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  </div>
</template>

<script>
import dataStorageOptions from "@/domain/core/constant/dataStorageOptions";
import {mapActions} from "vuex";
import PAlert from "@/components/PAlert.vue";

export default {
  name: "DataStorage",
  components: {PAlert},
  props: {
    data: {
      type: Object,
      default: () => ({
        provider: dataStorageOptions.default(),
        config: {
          endpoint: '',
          pattern: ''
        }
      })
    },
    formFields: {
      type: Array,
      default: () => []
    },
    picaServiceId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedStorage: dataStorageOptions.default(),
      jsonConfig: {
        endpoint: '',
        pattern: ''
      },
      jsonPreview: '',
      isJsonValid: true,
      checkingLastJson: false,
      lastJsonResult: '',
      lastJsonError: '',
      updatePreviewTimer: null
    }
  },
  computed: {
    dataStorageOptions() {
      return dataStorageOptions
    },
    storageOptions() {
      return dataStorageOptions.options().map(option => ({
        label: option.name,
        value: option.id
      }))
    },
    jsonPatternPlaceholder() {
      // Use v-pre or escape the braces to prevent Vue from interpreting them
      return '{\n  "customer": {\n    "cognome": "' + '{{last_name}}' + '",\n    "first_name": "' + '{{first_name}}' + '",\n    "registration_date": "' + '{{created_at|iso}}' + '",\n    "formatted_date": "' + '{{created_at|\'DD/MM/YYYY\'}}' + '"\n  }\n}'
    },
    testEndpointUrl() {
      const baseUrl = process.env.VUE_APP_PICA_CORE_API_BASE_URL || ''
      const serviceId = this.picaServiceId || '{pica_service_id}'
      return `${baseUrl}/dashboard/v2/form/${serviceId}/test/json_store/`
    }
  },
  emits: ['update:data'],
  watch: {
    data: function () {
      this.init()
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    ...mapActions('eventWizard', [
      'getTestJsonStored'
    ]),
    init() {
      this.selectedStorage = this.data?.provider ?? dataStorageOptions.default()
      this.jsonConfig = this.data?.config ?? {
        endpoint: '',
        pattern: ''
      }
      if (this.selectedStorage === dataStorageOptions.JSON_ENDPOINT) {
        this.updateJsonPreview()
      }
    },
    onStorageChange(event) {
      this.selectedStorage = event.target.value
      this.save()
    },
    onPatternInput() {
      // Debounce the preview update to prevent focus loss
      if (this.updatePreviewTimer) {
        clearTimeout(this.updatePreviewTimer)
      }
      this.updatePreviewTimer = setTimeout(() => {
        this.updateJsonPreview()
      }, 300)
      this.save()
    },
    updateJsonPreview() {
      if (!this.jsonConfig.pattern) {
        this.jsonPreview = ''
        this.isJsonValid = true
        return
      }

      let preview = this.jsonConfig.pattern
      
      // Handle special system fields and date modifiers
      preview = preview.replace(/{{created_at(\|[^}]+)?}}/g, (match, modifier) => {
        const now = new Date()
        if (modifier === '|iso') {
          return now.toISOString()
        } else if (modifier && modifier.startsWith('|')) {
          // Extract format string (remove | and quotes)
          const format = modifier.substring(1).replace(/['"]([^'"]+)['"]/, '$1')
          // Return formatted date - in preview we'll show example formats
          return this.getFormattedDateExample(format, now)
        }
        return now.toISOString() // Default to ISO format
      })
      
      // Replace all {{FIELD_NAME}} with dummy values based on field type
      const regex = /{{(\w+)(\|[^}]+)?}}/g
      preview = preview.replace(regex, (match, fieldKey, modifier) => {
        // Skip if already processed (like created_at)
        if (fieldKey === 'created_at') return match
        
        const field = this.formFields.find(f => f.key === fieldKey)
        if (field) {
          // Handle date fields with modifiers
          if (field.type === 'date' && modifier) {
            const sampleDate = new Date('2024-01-15')
            if (modifier === '|iso') {
              return sampleDate.toISOString()
            } else if (modifier.startsWith('|')) {
              const format = modifier.substring(1).replace(/['"]([^'"]+)['"]/, '$1')
              return this.getFormattedDateExample(format, sampleDate)
            }
          }
          
          // Generate dummy value based on field type
          switch (field.type) {
            case 'email':
              return 'user@example.com'
            case 'phone':
              return '+39 123 456 7890'
            case 'number':
              return '12345'
            case 'date':
              return '2024-01-15'
            case 'checkbox':
              return 'true'
            case 'select':
              // Return first option value if available
              if (field.params && field.params.options && field.params.options.length > 0) {
                return field.params.options[0].value || field.params.options[0]
              }
              return 'option1'
            case 'text':
            default:
              return 'Sample Text'
          }
        }
        return match // Keep original if field not found
      })

      this.jsonPreview = preview
      
      // Validate JSON
      try {
        JSON.parse(preview)
        this.isJsonValid = true
      } catch (e) {
        this.isJsonValid = false
      }
    },
    getFormattedDateExample(format, date) {
      // Common moment.js format examples
      const formats = {
        'YYYY-MM-DD': date.toISOString().split('T')[0],
        'DD/MM/YYYY': `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`,
        'MM/DD/YYYY': `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`,
        'YYYY-MM-DD HH:mm:ss': date.toISOString().replace('T', ' ').split('.')[0],
        'DD-MM-YYYY': `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`,
        'MMMM D, YYYY': date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        'D MMMM YYYY': date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).replace(/([A-Za-z]+) (\d+), (\d+)/, '$2 $1 $3'),
        'YYYY': String(date.getFullYear()),
        'MM': String(date.getMonth() + 1).padStart(2, '0'),
        'DD': String(date.getDate()).padStart(2, '0')
      }
      
      // Return formatted example or a placeholder showing the format
      return formats[format] || `[${format}]`
    },
    save() {
      this.$emit('update:data', {
        provider: this.selectedStorage,
        config: this.jsonConfig
      })
    },
    copyTestUrl() {
      navigator.clipboard.writeText(this.testEndpointUrl).then(() => {
        this.$toasted.success(this.$pgettext('form_configurator.data_storage.json_endpoint', 'URL copied to clipboard'))
      }).catch(() => {
        this.$toasted.error(this.$pgettext('form_configurator.data_storage.json_endpoint', 'Failed to copy URL'))
      })
    },
    async checkLastJsonSent() {
      this.checkingLastJson = true
      this.lastJsonResult = ''
      this.lastJsonError = ''
      
      try {
        const data = await this.getTestJsonStored()
        this.lastJsonResult = JSON.stringify(data, null, 2)
      } catch (error) {
        this.lastJsonError = this.$pgettext('form_configurator.data_storage.json_endpoint', 'No JSON data found or error retrieving data')
        console.error('Error checking last JSON:', error)
      } finally {
        this.checkingLastJson = false
      }
    }
  }
}
</script>

<style scoped>
.data-storage {
  /* Additional styling can be added here if needed */
}

.preview-textarea {
  font-family: monospace;
  background-color: #f8f9fa;
}

.preview-textarea.is-valid {
  border-color: #28a745;
}

.preview-textarea.is-invalid {
  border-color: #dc3545;
}
</style>